// @ts-ignore
import { data as css } from './unit-measurements.css';

import ClosetUnitView from '../../views/closet-unit-view';
import DrawerView from '../../views/drawer-view';
import GenericAssetView from '../../views/generic-asset-view';
import ParametricPartView from '../../views/parametric-part-view';
import RenderLoop from "../../control-flow/render-loop";
import injectCSS from "../../html/inject-css";
import { Box3, OrthographicCamera, Vector3 } from "three";
import { ClosetUnit, ParametricPart } from "../../domain";
import { DrawingContext } from "../../view2d/drawing-context";
import { approxGreaterThan, getAvailableSlots, toDisplayLengthWithUnitsText } from "../../measurement";
import { calcOrthoScale } from '../../view3d/coordinates';
import { clone, OptionalProperties, update } from "../../json/optional-properties";
import { getDisplayScale } from "../../view2d/get-display-scale";
import { pairwise } from '../../control-flow/pairwise';
import visibleSize from '../../view3d/visible-size';

export type UnitMeasurementsConfig = {
    lineWidth: number, // CSS pixels
    strokeStyle: string,
    textSize: number, // CSS pixels
    textFillStyle: string,
    textStrokeSize: number, // CSS pixels
    textStrokeStyle: string,
    markerSize: number, // CSS pixels
    units: "inches" | "mm",
};

export type UnitMeasurementsOptions = OptionalProperties<UnitMeasurementsConfig>;

const unitMeasurementsDefaults: UnitMeasurementsConfig = {
    lineWidth: 1,
    strokeStyle: "#808080",
    textSize: 13, // CSS pixels
    textFillStyle: "#808080",
    textStrokeSize: 4, // CSS pixels
    textStrokeStyle: "#FFFFFF",
    markerSize: 10, // CSS pixels
    units: "inches",
};

export class UnitMeasurements {

    showHeights: boolean = false;
    config: UnitMeasurementsConfig = clone(unitMeasurementsDefaults);

    camera: OrthographicCamera;
    model: ClosetUnit;
    view: ClosetUnitView;

    canvas: HTMLCanvasElement;
    observer: ResizeObserver;
    dc: DrawingContext = {
        scale: 1,
        displayScale: 1,
        ox: 0,
        oy: 0,

        ctx: null,
    };
    loop: RenderLoop;

    constructor(view: ClosetUnitView, camera: OrthographicCamera, options?: UnitMeasurementsOptions) {
        injectCSS(css);
        this.view = view;
        this.camera = camera;

        this.canvas = document.createElement('canvas');
        this.canvas.classList.add('unit-measurements');
        this.dc.ctx = this.canvas.getContext('2d');

        update(this.config, options);
        this.loop = new RenderLoop(this.render);

        this.observer = new ResizeObserver(this.resize);
    }

    start() {
        this.canvas.width = this.canvas.clientWidth * devicePixelRatio;
        this.canvas.height = this.canvas.clientHeight * devicePixelRatio;
        this.dc.displayScale = getDisplayScale(this.canvas);

        this.loop.start();
        this.observer.observe(this.canvas);
        this.canvas.classList.remove('hidden');
    }

    stop() {
        this.loop.stop();
        this.observer.disconnect();
        this.canvas.classList.add('hidden');
    }
    
    render = () => {
        const
            config = this.config,
            dc = this.dc,
            ctx = dc.ctx,
            v = new Vector3(0, 0, 0),
            box = new Box3();
        ctx.resetTransform();
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        this.dc.scale = calcOrthoScale(this.camera, this.canvas);

        // The projection produces normalized device coordinates, which assume
        // (0, 0) at the center of the canvas and a normalized width of 2, i.e.
        // ranging from -1 to 1 on each axis.
        v.project(this.camera);
        this.dc.ox = (v.x+1)/2 * this.canvas.width;
        this.dc.oy = this.canvas.height - (v.y+1)/2 * this.canvas.height;

        ctx.translate(this.dc.ox, this.dc.oy);
        ctx.scale(this.dc.scale, this.dc.scale);

        const
            rightSide = this.model.width + this.model.thickness * 2,
            scaledTextSize = config.textSize * dc.displayScale / dc.scale,
            measureLineOffset = 2 * scaledTextSize,
            extensionLineLength = measureLineOffset + scaledTextSize/2,
            intervals = new Intervals(
                this.model.content.position.y,
                this.model.content.position.y + this.model.height),
            fixed: number[] = [];

        this.model.content.traverse((model) => {
            if(model instanceof ParametricPart && model.name?.includes('Fixed')) {
                fixed.push(model.position.y);
            }
        });

        this.view.traverse((obj) => {
            if(obj instanceof ParametricPartView
               || obj instanceof GenericAssetView
               || obj instanceof DrawerView)
            {
                box.makeEmpty();
                visibleSize(obj, box);
                intervals.void(box.min.y, box.max.y);
            }
        });

        if(this.showHeights) {
            const
                startOffset = scaledTextSize/4,
                width = 5 * config.lineWidth * dc.displayScale / dc.scale;
            for(const y of getAvailableSlots()) {
                // left side
                drawExtensionLine(
                    this.dc, config,
                    -startOffset,
                    -startOffset - width,
                    -y);
                // right side
                drawExtensionLine(
                    this.dc, config,
                    rightSide + startOffset,
                    rightSide + startOffset + width,
                    -y);
            }
        }

        for(const [start, end] of intervals) {
            drawExtensionLine(
                this.dc, config, -scaledTextSize/4, -extensionLineLength, -start);
            drawExtensionLine(
                this.dc, config, -scaledTextSize/4, -extensionLineLength, -end);
            drawMeasureLine(
                this.dc, config, -measureLineOffset, -start, -measureLineOffset, -end, "left");
        }
        for(const y of intervals.zeroes()) {
            drawExtensionLine(
                this.dc, config, -scaledTextSize/4, -extensionLineLength, -y);
        }

        fixed.sort((a, b) => a - b);

        for(const [a, b] of pairwise(fixed)) {
            drawMeasureLine(
                this.dc, config,
                rightSide + measureLineOffset,
                -a,
                rightSide + measureLineOffset,
                -b,
                "right");
        }

        for(const y of fixed) {
            drawExtensionLine(
                this.dc, config, rightSide + scaledTextSize/4, rightSide + extensionLineLength, -y);
            drawMeasureText(
                this.dc, config, rightSide + extensionLineLength, -y, "right", y);
        }
    };

    resize = () => {
        this.loop.onBeforeRender.add(() => {
            this.canvas.width = this.canvas.clientWidth * devicePixelRatio;
            this.canvas.height = this.canvas.clientHeight * devicePixelRatio;
            this.dc.displayScale = getDisplayScale(this.canvas);
        });
        this.loop.update();
    }

    setShowHeights(showHeights: boolean) {
        this.showHeights = showHeights;
        this.loop.update();
    }
}

type Interval = { start: number, end: number };

/**
 * A helper for constructing the measure lines on the left side that show the
 * distance between objects.
 *
 * This is constructed with an initial interval on the number line and then you
 * can progressively add voids in that interval. At the end you can iterate
 * over the non-void intervals that remain.
 */
class Intervals {
    start: number;
    end: number;
    voids: Interval[] = [];

    constructor(start: number, end: number) {
        this.start = start;
        this.end = end;
    }

    void(start: number, end: number) {
        this.voids.push({start, end});
    }

    *[Symbol.iterator](): Generator<[start: number, end: number]> {
        this.voids.sort((a, b) => a.start - b.start);

        let x = this.start;
        for(const item of this.voids) {
            if(approxGreaterThan(item.start, x)) { // item.start > x
                yield [x, item.start];
            }
            x = item.end;
        }
        if(approxGreaterThan(this.end, x)) { // this.end > x
            yield [x, this.end];
        }
    }

    *zeroes(): Generator<number> {
        const
            ends = new Set<number>(this.voids.map(v => round(v.end))),
            starts = Array.from(this.voids.map(v => round(v.start)));
        starts.sort((a, b) => a - b);
        for(const x of starts) {
            if(ends.has(x)) {
                yield x;
            }
        }
    }
}

const round = (x: number) => Math.round(32*x)/32;

export const drawMeasureLine = (
    dc: DrawingContext,
    config: UnitMeasurementsConfig,
    startX: number, // inches
    startY: number,
    endX: number,
    endY: number,
    side: "left" | "right"
) => {
    const
        ctx = dc.ctx,
        dx = endX - startX,
        dy = endY - startY,
        length = Math.sqrt(dx * dx + dy * dy),
        midX = (startX + endX) / 2,
        midY = (startY + endY) / 2;

    ctx.save();

    ctx.lineWidth = config.lineWidth * dc.displayScale / dc.scale;
    ctx.lineJoin = "round";
    ctx.strokeStyle = config.strokeStyle;

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    const lengthLimit = 2 * config.markerSize * dc.displayScale / dc.scale;
    if(length > lengthLimit) {
        drawMeasureLineMarker(dc, config.markerSize, config.strokeStyle, true, startX, startY);
        drawMeasureLineMarker(dc, config.markerSize, config.strokeStyle, false, endX, endY);
        drawMeasureText(dc, config, midX, midY, side, length);
    } else {
        drawMeasureText(dc, config, midX, midY, side, length);
    }

    ctx.restore();
};

export const drawMeasureLineMarker = (
    dc: DrawingContext,
    markerSize: number, // CSS pixels
    fillStyle: string,
    up: boolean,
    x: number, // inches
    y: number,
) => {
    const
        ctx = dc.ctx,
        scaledSize = (up ? -1 : 1) * markerSize * dc.displayScale / dc.scale,
        quarter = Math.abs(scaledSize) / 4;
    ctx.save();
    ctx.fillStyle = fillStyle;
    ctx.translate(x, y);
    ctx.beginPath();
    ctx.moveTo(-quarter, scaledSize);
    ctx.lineTo(0, 0)
    ctx.lineTo(quarter, scaledSize);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
};

const drawExtensionLine = (
    dc: DrawingContext,
    config: UnitMeasurementsConfig,
    x1: number,
    x2: number,
    y: number,
) => {
    const ctx = dc.ctx;
    ctx.save();

    ctx.lineWidth = config.lineWidth * dc.displayScale / dc.scale;
    ctx.strokeStyle = config.strokeStyle;

    ctx.beginPath();
    ctx.moveTo(x1, y);
    ctx.lineTo(x2, y);
    ctx.stroke();
    ctx.restore();
};

const drawMeasureText = (
    dc: DrawingContext,
    config: UnitMeasurementsConfig,
    midX: number,
    midY: number,
    side: "left" | "right",
    length: number,
) => {
    const
        ctx = dc.ctx,
        textScaledSize = config.textSize * dc.displayScale / dc.scale,
        offsetX = textScaledSize,
        textStrokeScaledSize = config.textStrokeSize * dc.displayScale / dc.scale;

    ctx.save();

    ctx.textAlign = side == "left" ? "right" : "left";
    const sign = side == "left" ? -1 : 1;

    ctx.textBaseline = 'middle';
    ctx.font = `${textScaledSize}px sans-serif`;
    ctx.fillStyle = config.textFillStyle;
    ctx.lineWidth = textStrokeScaledSize;
    ctx.strokeStyle = config.textStrokeStyle;

    let formattedLength: string = toDisplayLengthWithUnitsText(length, config.units);

    ctx.strokeText(formattedLength, midX + sign * offsetX, midY);
    ctx.fillText(  formattedLength, midX + sign * offsetX, midY);

    ctx.restore();
};

